import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AbstractStateInterface } from '../../interface/abstract';
import { BtdCreateReq, BtdInterface, BtdUpdateReq } from '../../interface/bloodtypedestiny';
import { ApiService } from '../../utils/apiService';
import { InterfaceToSearchParams } from '../../utils/helper';

type DataState = AbstractStateInterface<BtdInterface>;

const initialState: DataState = {
  data: [],
  detail: null,
  loading: false,
};

export interface BtdSearch {
  id?: string;
}

export const createBtd = createAsyncThunk('admin-api/blood_type_destiny', async (payload: BtdCreateReq, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/blood_type_destiny`, 'POST', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const getAllBtds = createAsyncThunk('admin-api/blood_type_destiny/all', async (_, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/blood_type_destiny/all`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});
export const findBtdByCondition = createAsyncThunk(
  'admin-api/blood_type_destiny/get',
  async (payload: BtdSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/blood_type_destiny?${searchParams}`, 'GET', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const updateBtd = createAsyncThunk(
  'admin-api/blood_type_destiny/put',
  async (payload: BtdUpdateReq, thunkAPI) => {
    const { id, ...data } = payload;
    try {
      const response = await ApiService(`admin-api/blood_type_destiny/${id}`, 'PUT', data);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const deleteBtd = createAsyncThunk(
  'admin-api/blood_type_destiny/delete',
  async (payload: BtdSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/blood_type_destiny?${searchParams}`, 'DELETE', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

const btdSlice = createSlice({
  name: 'blood_type_destiny',
  initialState,
  reducers: {
    resetBtdSlice: () => {
      return { ...initialState };
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllBtds.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllBtds.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.data = action.payload.data as BtdInterface[];
        } else {
          state.data = [];
        }
      })
      .addCase(getAllBtds.rejected, (state) => {
        state.loading = false;
      })
      .addCase(findBtdByCondition.pending, (state) => {
        state.loading = true;
      })
      .addCase(findBtdByCondition.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.detail = action.payload.data[0] as BtdInterface;
        } else {
          state.detail = null;
        }
      })
      .addCase(findBtdByCondition.rejected, (state) => {
        state.loading = false;
      });
  },
});
export const { resetBtdSlice, resetDetail } = btdSlice.actions;
export default btdSlice.reducer;
